import React, { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { HeaderNavigation } from '@/components/HeaderNavigation'
import { UserProfileInfo } from '@/components/UserProfileInfo'
import { AppButton } from '@/components/AppButton'
import { useFeatureFlag } from '@/hooks'

interface ProfileNonUserParams {
  id: string
  avatarUrl?: string
  firstName?: string
  lastName?: string
  phoneNumber: string
  avatarBg?: string[]
}

export const ProfileNonUserScreen: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [isInviting, setIsInviting] = useState(false)

  // Get params from navigation state
  const params = location.state as ProfileNonUserParams
  const { id, avatarUrl, firstName, lastName, phoneNumber, avatarBg } =
    params || {}

  // Feature flags
  const isEnabledTransferToNonUsers = useFeatureFlag(
    'contacts_transfer_restriction_enabled',
  )

  // Mock data for guest contacts and feature settings
  const isInviteSent = false // TODO: Get from API
  const shouldDisplayReferralBonuses = true // TODO: Get from feature settings

  const handleInviteUser = useCallback(async () => {
    setIsInviting(true)
    try {
      // TODO: Implement invite user API call

      // Mock delay
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // TODO: Show success message
    } catch (error) {
      // TODO: Show error message
    } finally {
      setIsInviting(false)
    }
  }, [phoneNumber])

  const handleRedirectToSendRequest = useCallback(
    (type: 'send' | 'request') => {
      navigate('/send-request', {
        state: {
          type,
          recipient: {
            id,
            name: `${firstName} ${lastName}`.trim(),
            phoneNumber,
            avatar: avatarUrl,
            avatarBg,
          },
        },
      })
    },
    [navigate, id, firstName, lastName, phoneNumber, avatarUrl, avatarBg],
  )

  // If no params, show error
  if (!params || !phoneNumber) {
    return (
      <BaseView flex={1} bgColor="background.screen" className="h-full">
        <HeaderNavigation title={t('screens:profile.userProfile')} />
        <BaseView className="flex-1 justify-center items-center p-8">
          <BaseText
            fontSize={18}
            fontFamily="medium"
            color="label.default"
            className="text-center mb-4"
          >
            {t('common:userNotFound')}
          </BaseText>
          <AppButton variant="secondary" onPress={() => navigate(-1)}>
            {t('form:actions.goBack')}
          </AppButton>
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView flex={1} bgColor="background.screen" className="h-full">
      {/* Header */}
      <HeaderNavigation title={firstName || t('common:contact')} />

      {/* Content */}
      <BaseView
        flex={1}
        px={12}
        className={`${isEnabledTransferToNonUsers ? 'justify-center' : 'pt-12'}`}
        style={{
          paddingBottom: isEnabledTransferToNonUsers ? '140px' : '20px',
        }}
      >
        <BaseView className="space-y-8 pt-8">
          {/* User Profile Info */}
          <UserProfileInfo
            avatarUrl={avatarUrl}
            firstName={firstName}
            lastName={lastName}
            phoneNumber={phoneNumber}
            avatarBg={avatarBg}
          />
        </BaseView>
      </BaseView>

      {/* Fixed Bottom Actions - only if transfers to non-users are enabled */}
    </BaseView>
  )
}
